<template>
    <div class="h-screen w-full flex flex-col items-center justify-center">
        <div class="md:border w-full max-w-md flex flex-col items-center px-12 py-14 rounded-3xl gap-6">
            <span class="text-3xl text-center font-semibold tracking-wide text-primary">Verify Your Email</span>
            <img src="@/assets/img/email-verify.png" class="h-60" alt="">
            <span class="text-sm font-medium tracking-wide leading-relaxed text-center text-secondary">Enter the 6-digit
                verification code we just sent to your email address.</span>
            <div class="w-full flex flex-col gap-10">
                <div class="flex w-full justify-between">
                    <input v-for="(digit, index) in otp" :key="index" v-model="otp[index]" maxlength="1"
                        @input="handleInput($event, index)" @keydown.backspace="handleBackspace($event, index)"
                        class="border border-gray-300 rounded-md w-12 h-12 text-center text-xl focus:outline-none focus:border-primary" />
                </div>
                <a-button :loading="isLoading" @click="onVerify" class="w-full" type="primary" shape="round" size="large">
                    Verify
                </a-button>
            </div>
            <span class="text-[.8rem] tracking-wide text-center text-secondary">
                Didn't receive the Code? 
                <span @click="onResend" class="text-primary font-medium cursor-pointer" 
                      :class="{'cursor-not-allowed': isDisabled}" :style="{ opacity: isDisabled ? 0.5 : 1 }">
                    {{ isDisabled ? `Resend in ${formattedTime}` : (isLoadingResend ? 'Please Wait...' : 'Resend') }}
                </span>
            </span>
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { message } from 'ant-design-vue'
import { useRoute, useRouter } from 'vue-router'
import AuthApi from "../../../api/auth"
import useAuth from "@/composables/useAuth"

const authApi = new AuthApi()
const route = useRoute()
const router = useRouter()

const otp = ref(['', '', '', '', '', ''])
const isLoading = ref(false)
const isLoadingResend = ref(false)
const timer = ref(0) // Timer for resend functionality
const isDisabled = ref(false) // Control resend button disable state
const formattedTime = ref("05:00") // Formatted time in MM:SS

// Handle OTP input and navigation between fields
const handleInput = (event, index) => {
    const input = event.target
    const value = input.value

    if (value.match(/[0-9]/)) {
        otp.value[index] = value
        if (index < otp.value.length - 1) {
            event.target.nextElementSibling?.focus()
        }
    } else {
        otp.value[index] = ''
    }
}

const handleBackspace = (event, index) => {
    if (!otp.value[index] && index > 0) {
        event.target.previousElementSibling?.focus()
    }
}

// Function to start the resend timer with MM:SS format
const startTimer = () => {
    timer.value = 300 // Set 5 minutes (300 seconds)
    isDisabled.value = true

    const interval = setInterval(() => {
        if (timer.value > 0) {
            timer.value -= 1
            const minutes = Math.floor(timer.value / 60).toString().padStart(2, '0')
            const seconds = (timer.value % 60).toString().padStart(2, '0')
            formattedTime.value = `${minutes}:${seconds}`
        } else {
            isDisabled.value = false
            clearInterval(interval)
            formattedTime.value = "05:00" // Reset timer display after countdown
        }
    }, 1000)
}

onMounted(() => {
    if (route.query.pre) {
        onResend()
    }
})

const getProfile = async (token) => {
    try {
        const r = await authApi.getProfile()
        useAuth().set(JSON.stringify(r.data), useAuth().get().token, true)

        const redirectTo = sessionStorage.getItem('redirectAfterLogin') || '/Internal/Core';
        sessionStorage.removeItem('redirectAfterLogin');

        router.replace({ path: redirectTo })
    } catch (error) {
        message.error(error?.response?.data?.message ?? 'An error occurred. Please try again later.')
    }
}

// OTP verification function
const onVerify = async () => {
    if (otp.value.every(digit => digit !== '')) {
        isLoading.value = true
        try {
            const otpCode = otp.value.join('')
            await authApi.verifyOtp({ otp: otpCode })
            message.success("Successfully Verified Email!")

            getProfile()
        } catch (error) {
            message.error(error?.response?.data?.message ?? 'An error occurred. Please try again later.')
        } finally {
            isLoading.value = false
        }
    }
}

const onResend = async () => {
    if (!isDisabled.value) {
        isLoadingResend.value = true
        try {
            await authApi.resendOtp()
            startTimer() 
        } catch (error) {
            message.error(error?.response?.data?.message ?? 'An error occurred. Please try again later.')
        } finally {
            isLoadingResend.value = false
        }
    }
}
</script>
